import { AutocompleteAPI, DatePickerAPI, DatePickerData, DeckAPI, FetchAPI, FilterAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnChange, OnClick, OnMount, OnNavigate, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, ClickEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { Inject } from '@odinjs/odin';
import moment, { Moment as MomentType } from 'moment/moment.js';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { APPOINTMENT_TYPE } from '../../constants/appointment-type';
import { ScheduleType } from '../../constants/schedule-type';
import { DatePickerNavigate } from '../../interfaces/date-picker';
import { SideBarContext } from '../../interfaces/side-bar';
import AppointmentService from '../../services/appointment-service';
import { DisplayRule } from '../../interfaces/scheduling-display-rules';
import { AppointmentUtils } from '../../utils/appointment-utils';
import { CONTEXT_ATTRIBUTE } from '../../constants/context-attribute';
import BaseController from '../BaseController';
import UserContext from '../../utils/user-context';

import './side-bar-controller.css';
import { APPLICATION_ROUTES } from '../../constants/application-routes';

const routes = [
  APPOINTMENT_TYPE.CHEMOTHERAPIES,
  APPOINTMENT_TYPE.CONSULTATIONS,
  APPOINTMENT_TYPE.EXAMS,
  APPOINTMENT_TYPE.SERVICES,
  APPOINTMENT_TYPE.SURGERIES,
  APPOINTMENT_TYPE.NEW_APPOITMENT,
  APPOINTMENT_TYPE.REQUEST,
  APPOINTMENT_TYPE.RESCHEDULE
];

let defaultDisplayRules: Record<string, Value>[] = [];

@Controller({ component: 'layout-side-bar', fragment: 'side-bar'  })
export default class SideBarController extends BaseController {

  private currentRoute!: string;

  @Inject('date-picker-my-calendar')
    datePickerMyCalendar!: DatePickerAPI;

  @Inject('fetch-appointments-small')
    fetchAppointmentsSmall!: FetchAPI;

  @Inject('deck-appointments-small')
    deckAppointment!: DeckAPI;

  @Inject('panel-card-list')
    panelCardList!: PanelAPI;

  @Inject('panel-card-list-filter')
    panelCardListFilterApi!: PanelAPI;

  @Inject('autocomplete-categories')
    autocompleteCategoriesApi!: AutocompleteAPI;

  @Inject('filter-card-list')
    filterCardListApi!: FilterAPI;

  @OnMount({ target: 'fetch-appointments-small' })
  onMountPanelCardList(): void {
    this.currentRoute = this.historyService.getCurrentRoute();
    const appointmentContext = AppointmentUtils.getAppointmentContext();
    const initialDate = appointmentContext.datePickerValue ?
      moment(appointmentContext.datePickerValue, ISO_DATE_FORMAT) : moment();
    this.datePickerMyCalendar.setValue(initialDate.utc().toISOString());
    this.datePickerMyCalendar.setInitialDate(initialDate.utc().toISOString());
    this.setVisibleCardList();
  }

  @OnMount({ target: 'date-picker-my-calendar'})
  onMountDatePickerMyCalendar(): void {
    const appointmentContext = AppointmentUtils.getAppointmentContext();
    const actualDate = appointmentContext && appointmentContext.selectedDate ?
      moment(appointmentContext.selectedDate, ISO_DATE_FORMAT) : moment();
    const initialDate = actualDate.startOf('month').format(ISO_DATE_FORMAT);
    const finalDate = actualDate.endOf('month').format(ISO_DATE_FORMAT);

    this.fetchDatePickerData(initialDate, finalDate);
    this.datePickerMyCalendar.setValue(appointmentContext.datePickerValue ?
      appointmentContext.datePickerValue : actualDate.utc().toISOString());
  }

  @OnNavigate({ target: 'date-picker-my-calendar' })
  handleDatePickerNavigate(navigate: DatePickerNavigate): void {
    if (this.isNewAppointmentOrRescheduleRoute()) {
      return;
    }

    const initialDate = moment(navigate.to[0], ISO_DATE_FORMAT);
    const finalDate = moment(navigate.to[1], ISO_DATE_FORMAT);

    this.setContextAppointmentDetail({
      datePickerNavigate: navigate,
      finalDate,
      initialDate,
      navigate,
    });
    this.fetchDatePickerData(initialDate.format(ISO_DATE_FORMAT), finalDate.format(ISO_DATE_FORMAT));
  }

  @OnChange({ target: 'date-picker-my-calendar' })
    handleChangeDatePickerValue = (event: ChangeEvent<string>): void => {
      if (this.isNewAppointmentOrRescheduleRoute()) {
        return;
      }

      const selectedDate = moment(event.value, ISO_DATE_FORMAT);
      const appointmentDetail = AppointmentUtils.getAppointmentContext() as SideBarContext;
      const filters = appointmentDetail && appointmentDetail.filters ? appointmentDetail.filters : [];

      this.datePickerMyCalendar.setValue(event.value);
      setTimeout(() => this.setContextAppointmentDetail({
        datePickerValue: event.value,
        filters,
        selectedDate,
      }));

      this.delegateAction(() => this.fetchDataList(selectedDate, filters));
    }

  delegateAction = (method: () => void): void => {
    if (this.isDetailRoute()) {
      const { patientId } = UserContext;
      this.historyService.push(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENTS, { patientId });
    } else {
      method();
    }
  }

  setVisibleCardList(): void {
    this.panelCardList.setVisible(this.isDetailRoute());
  }

  isDetailRoute(): boolean {
    return routes.filter(item => this.currentRoute.includes(item)).length > 0;
  }

  setContextAndPushHistory(appointmentType: APPOINTMENT_TYPE, event: ClickEvent ): void {
    const { nrSequencia, ieScheduleType } = event.value;
    const { patientId } = UserContext;
    const params = {
      appointmentType,
      ieScheduleType,
      nrSeq: nrSequencia,
      patientId,
    };

    this.historyService.push(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENT_DETAIL, params);
  }

  @OnClick({ target: ['deck-appointments-small'] })
  onCardDeckClick(event: ClickEvent): void {
    if (this.isNewAppointmentOrRescheduleRoute()) {
      return;
    }

    if (event && event.value) {
      const { ieScheduleType } = event.value;

      this.setContextAppointmentDetail({
        isItemSelected: true,
      });

      if(!this.currentRoute.includes(APPOINTMENT_TYPE.NEW_APPOITMENT)) {
        switch(ieScheduleType) {
          case ScheduleType.CONSULTATION: this.setContextAndPushHistory(APPOINTMENT_TYPE.CONSULTATIONS, event); break;
          case ScheduleType.EXAM: this.setContextAndPushHistory(APPOINTMENT_TYPE.EXAMS, event); break;
          case ScheduleType.SERVICE: this.setContextAndPushHistory(APPOINTMENT_TYPE.SERVICES, event); break;
          case ScheduleType.SURGERY: this.setContextAndPushHistory(APPOINTMENT_TYPE.SURGERIES, event); break;
          case ScheduleType.CHEMOTHERAPY: this.setContextAndPushHistory(APPOINTMENT_TYPE.CHEMOTHERAPIES, event); break;
          case ScheduleType.REQUEST: this.setContextAndPushHistory(APPOINTMENT_TYPE.REQUEST, event); break;
        }
      }
    }
  }

  addFilterAndFetch(filters: DisplayRule[] = []): void {
    const appointmentDetail = AppointmentUtils.getAppointmentContext() as SideBarContext;
    const datePickerValue = appointmentDetail && appointmentDetail.datePickerValue;
    const initialDate = datePickerValue ? (moment(appointmentDetail.datePickerValue, ISO_DATE_FORMAT)) : undefined;
    const filterValues = filters.map(item => item.value);

    this.fetchDataList(initialDate, filterValues);
    this.setContextAppointmentDetail({
      datePickerValue,
      filters,
    });
  }

  fetchDatePickerData(initialDate: string, finalDate: string): Promise<boolean> {
    const patientId = UserContext.patientId.toString();
    const { datePickerMyCalendar } = this;

    return AppointmentService.getCountAppointmentsInPeriod(patientId, initialDate, finalDate)
      .then(datePickerData => {
        datePickerMyCalendar.setData(datePickerData as unknown as DatePickerData);
        return true;
      });
  }

  fetchDataList(startDate?: MomentType, filters?: Value[]): void {
    if (this.panelCardList && this.panelCardList.isVisible()) {
      const initialDate = startDate == null ? '' : startDate.format(ISO_DATE_FORMAT);
      const filter = filters == null ? [] : filters;

      this.fetchAppointmentsSmall.setParameters({
        filter,
        initialDate,
      });
      this.fetchAppointmentsSmall.forceFetch();
    }
  }

  setContextAppointmentDetail(attr: SideBarContext, clear?: boolean): void {
    const appointmentDetailContext = this.context.get(CONTEXT_ATTRIBUTE.SIDEBAR_CONTEXT) as SideBarContext;
    const appContext = getFoundationDataContext();
    attr.bindOrigin = this.historyService.getMatch().pathname;

    if (clear) {
      this.context.set({
        [CONTEXT_ATTRIBUTE.SIDEBAR_CONTEXT]: {}
      });
      return;
    }

    const params = {
      [CONTEXT_ATTRIBUTE.SIDEBAR_CONTEXT]: {
        ...appointmentDetailContext,
        ...attr
      }
    };

    this.context.set(params);
    appContext.set(params);
  }

  isNewAppointmentOrRescheduleRoute(): boolean {
    return this.currentRoute.includes(APPOINTMENT_TYPE.NEW_APPOITMENT)
    || this.currentRoute.includes(APPOINTMENT_TYPE.RESCHEDULE)
  }

  handleFilterChange(filterCategories: DisplayRule[]): void {
    const appointmentDetailContext = this.context.get(CONTEXT_ATTRIBUTE.SIDEBAR_CONTEXT) as SideBarContext;
    const isFilterChanged = appointmentDetailContext && appointmentDetailContext.filterChanged;
    const areDisplayRulesLoaded = appointmentDetailContext && appointmentDetailContext.displayRulesLoaded;

    if (this.isNewAppointmentOrRescheduleRoute() && areDisplayRulesLoaded) {
      return;
    }

    if (isFilterChanged) {
      this.delegateAction(() => this.addFilterAndFetch(filterCategories));
    } else {
      this.addFilterAndFetch(filterCategories);
    }
  }

  @OnMount({ target: 'autocomplete-categories' })
  onMountAutocomplete(): void {
    this.autocompleteCategoriesApi.setData(defaultDisplayRules);
  }

  @OnMount({ target: 'filter-card-list' })
  onMountFilter(): void {
    const { panelCardList, filterCardListApi, panelCardListFilterApi } = this;
    AppointmentUtils.getDisplayConfig().then(displayRules => {
      if (panelCardList.isVisible()) {
        defaultDisplayRules = displayRules;
        const appointmentContext = AppointmentUtils.getAppointmentContext();
        const filterValues: DisplayRule[] = appointmentContext.filters as DisplayRule[];
        const categories = filterValues && filterValues.length > 0 ? filterValues : defaultDisplayRules;
        filterCardListApi.setData({ categories });
        this.handleFilterChange(filterValues);
        this.setContextAppointmentDetail({ displayRulesLoaded: true });

        if (this.isNewAppointmentOrRescheduleRoute()) {
          panelCardListFilterApi.setVisible(false);
        }
      }
    })
  }

  @OnSubmit({ target: 'filter-card-list' })
  onSubmitFilter(event: SubmitEvent): void {
    const filterCategories = event.record.categories as DisplayRule[]
    const appointmentDetailContext = this.context.get(CONTEXT_ATTRIBUTE.SIDEBAR_CONTEXT) as SideBarContext;
    const areDisplayRulesLoaded = appointmentDetailContext && appointmentDetailContext.displayRulesLoaded;

    if (areDisplayRulesLoaded) {
      this.handleFilterChange(filterCategories);
      this.setContextAppointmentDetail({ filterChanged: true, filters: filterCategories })
    }
  }

  @OnBeforeSubmit({ target: 'filter-card-list' })
  onBeforeSubmitFilter(event: AbortableSubmitEvent): void {
    const appointmentDetailContext = this.context.get(CONTEXT_ATTRIBUTE.SIDEBAR_CONTEXT) as SideBarContext;
    const areDisplayRulesLoaded = appointmentDetailContext && appointmentDetailContext.displayRulesLoaded;

    if (this.isNewAppointmentOrRescheduleRoute() && areDisplayRulesLoaded) {
      event.abort();
    }
  }
}
